import { computeHabitEntryUpdate } from '@griply/utils';
import { useCallback } from 'react';
import { useOpenTaskDetail } from 'shared/contexts/task-detail';
import { useToday } from 'shared/contexts/today';
import { useUpdateTasksMutation } from 'shared/hooks/queries/use-update-tasks-mutation';
import { useTrackEvents } from 'shared/hooks/use-track-events';
import {
  ActionEvents,
  taskTypeToTrackingTaskType,
} from 'shared/services/tracking';
import { Habit } from 'shared/types/habit';
import { ScheduleEntryState } from 'shared/types/habit-schedule';
import { RepeatingTask } from 'shared/types/repeating-task';
import { Timestamp } from 'shared/types/timestamp';

export type Options = {
  value?: number;
  increment?: number;
  toggle?: boolean;
  keepDetailOpen?: boolean;
};

export const useUpdateHabitScheduleEntry = () => {
  const today = useToday();
  const { mutate: updateTasks } = useUpdateTasksMutation();
  const track = useTrackEvents();
  const openTaskDetail = useOpenTaskDetail();

  return useCallback(
    (
      habit: Habit | RepeatingTask,
      date: Timestamp = new Date(),
      type: ScheduleEntryState = ScheduleEntryState.Complete,
      { value, increment, toggle, keepDetailOpen }: Options = {},
    ) => {
      const { newSchedules, shouldCompleteHabit, ignored } =
        computeHabitEntryUpdate(habit, date, {
          today,
          type,
          value,
          increment,
          toggle,
        });

      if (ignored) {
        return;
      }

      if (shouldCompleteHabit) {
        track(ActionEvents.TaskComplete, {
          type: taskTypeToTrackingTaskType[habit.type],
        });
      }

      // Update the tasks and finish
      updateTasks([
        { id: habit.id, schedules: newSchedules },
        // Uncomplete the subtasks
        ...(habit.childIds?.map((id) => ({
          id,
          completedAt: null,
        })) ?? []),
      ]);

      !keepDetailOpen && shouldCompleteHabit && openTaskDetail(undefined);
    },
    [openTaskDetail, today, track, updateTasks],
  );
};
